/**
 * idea.ts — Idea REST API helpers
 * Base URL: REACT_APP_API_URL (default: http://localhost:5000/api)
 * Endpoint: GET /ideas — list ideas with optional filters
 */

const BASE = process.env.REACT_APP_API_URL ?? "http://localhost:5000/api";

const authHeaders = (token: string) => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${token}`,
});

/** Submit a new idea for a hackathon. teamId is resolved server-side from the auth token. */
export async function submitIdea(
  token: string,
  payload: { hackathonId: string; title: string; problem: string; solution: string; attachments?: File[] }
) {
  const formData = new FormData();
  formData.append("hackathonId", payload.hackathonId);
  formData.append("title", payload.title);
  formData.append("problem", payload.problem);
  formData.append("solution", payload.solution);
  payload.attachments?.forEach((file) => formData.append("attachments", file));

  const res = await fetch(`${BASE}/ideas`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.message ?? "Failed to submit idea");
  return data;
}

/** Fetch ideas for the logged-in user's team. Optionally filter by teamId or hackathonId. */
export async function fetchIdeas(token: string, params?: { teamId?: string; hackathonId?: string; status?: string }) {
  const query = new URLSearchParams();
  if (params?.teamId) query.set("teamId", params.teamId);
  if (params?.hackathonId) query.set("hackathonId", params.hackathonId);
  if (params?.status) query.set("status", params.status);
  const res = await fetch(`${BASE}/ideas?${query.toString()}`, { headers: authHeaders(token) });
  if (!res.ok) throw new Error("Failed to fetch ideas");
  return res.json(); // { items, pagination }
}

/** Fetch a single hackathon event (for phase window dates) */
export async function fetchHackathonEvent(token: string, hackathonId: string) {
  const res = await fetch(`${BASE}/hackathon-events/${hackathonId}`, {
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
  });
  if (!res.ok) throw new Error("Failed to fetch hackathon");
  const json = await res.json();
  return json.data ?? json;
}

/** Final submission — updates problem/solution/attachments and sets status to submitted_final */
export async function finalSubmitIdea(
  token: string,
  ideaId: string,
  payload: { problem: string; solution: string; attachments?: File[] }
) {
  const formData = new FormData();
  formData.append("problem", payload.problem);
  formData.append("solution", payload.solution);
  payload.attachments?.forEach((file) => formData.append("attachments", file));

  const res = await fetch(`${BASE}/ideas/${ideaId}/final-submit`, {
    method: "PATCH",
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.message ?? "Final submission failed");
  return data;
}
